import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './ChatPad.css';

/**
 * ChatPad Component
 * Consistent AI agent chat interface following brandlive.json specifications
 */
const ChatPad = ({
  agentName = 'AI Assistant',
  agentEmoji = '🤖',
  agentColor = 'primary',
  placeholder = 'Type your message...',
  welcomeMessage,
  welcomeHints = [],
  onSendMessage,
  initialMessages = [],
  className = '',
}) => {
  const [messages, setMessages] = useState(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Add welcome message on mount
  useEffect(() => {
    if (welcomeMessage && messages.length === 0) {
      setMessages([
        {
          id: 'welcome',
          sender: 'agent',
          text: welcomeMessage,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    }
  }, [welcomeMessage, messages.length]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: inputValue.trim(),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    const nextMessages = [...messages, userMessage];

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      const chatHistory = nextMessages.map((msg) => ({
        sender: msg.sender === 'user' ? 'user' : 'assistant',
        text: msg.text,
      }));

      const response = await onSendMessage({
        message: userMessage.text,
        chatHistory,
        context: {},
      });

      setIsTyping(false);

      const agentMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'agent',
        text: response.response || response.text || 'I apologize, but I encountered an error.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        suggestions: response.suggestions || [],
      };

      setMessages((prev) => [...prev, agentMessage]);
    } catch (error) {
      setIsTyping(false);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'agent',
        text: 'I apologize, but I encountered an error. Please try again.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleHintClick = (hint) => {
    setInputValue(hint);
    inputRef.current?.focus();
  };

  return (
    <div className={`chatpad-container ${className}`}>
      {/* Header */}
      <div className="chatpad-header">
        <div className="chatpad-agent-info">
          <span className={`chatpad-agent-avatar chatpad-agent-avatar--${agentColor}`}>
            {agentEmoji}
          </span>
          <span className="chatpad-agent-name">{agentName}</span>
        </div>
      </div>

      {/* Messages Container */}
      <div className="chatpad-messages">
        {messages.map((message) => (
          <div key={message.id} className={`chatpad-message chatpad-message--${message.sender}`}>
            <div className="chatpad-message-content">
              <div className="chatpad-message-text">{message.text}</div>
              {message.suggestions && message.suggestions.length > 0 && (
                <div className="chatpad-message-suggestions">
                  {message.suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      className="chatpad-suggestion-chip"
                      onClick={() => handleHintClick(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="chatpad-message-timestamp">{message.timestamp}</div>
          </div>
        ))}

        {isTyping && (
          <div className="chatpad-message chatpad-message--agent">
            <div className="chatpad-typing-indicator">
              <span className="chatpad-typing-dot" />
              <span className="chatpad-typing-dot" />
              <span className="chatpad-typing-dot" />
            </div>
          </div>
        )}

        {messages.length === 1 && welcomeHints.length > 0 && (
          <div className="chatpad-welcome-hints">
            <p className="chatpad-welcome-hints-label">Try asking:</p>
            <div className="chatpad-welcome-hints-grid">
              {welcomeHints.map((hint, idx) => (
                <button
                  key={idx}
                  className="chatpad-hint-button"
                  onClick={() => handleHintClick(hint)}
                >
                  {hint}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Composer */}
      <div className="chatpad-composer">
        <textarea
          ref={inputRef}
          className="chatpad-input"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          rows={1}
          disabled={isLoading}
        />
        <button
          className="chatpad-send-button"
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
          aria-label="Send message"
        >
          <i className="fa-solid fa-arrow-right" />
        </button>
      </div>
    </div>
  );
};

ChatPad.propTypes = {
  agentName: PropTypes.string,
  agentEmoji: PropTypes.string,
  agentColor: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'success', 'warning']),
  placeholder: PropTypes.string,
  welcomeMessage: PropTypes.string,
  welcomeHints: PropTypes.arrayOf(PropTypes.string),
  onSendMessage: PropTypes.func.isRequired,
  initialMessages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      sender: PropTypes.oneOf(['user', 'agent', 'system']),
      text: PropTypes.string,
      timestamp: PropTypes.string,
    })
  ),
  className: PropTypes.string,
};

export default ChatPad;
